import React, { useState, useEffect } from "react";
import { Table, Spin, message, Button, Modal, Form, Input, Upload, Select, Descriptions, Image } from "antd";
import { UploadOutlined, EyeOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { fetchInstructors, registerTeacher } from "../../ApiServices/getTeacherService";
import { educationLevelOptions, MAX_FILE_SIZE_MB, ACCEPTED_FILE_TYPES } from "../../Config/teacherConstants";
import { validateFile } from "../../Config/fileUtils";
import "./Teachers.css";
import axiosInstance from "../../Config/axiosConfig";

const { Option } = Select;

const Teachers = () => {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadInstructors = async () => {
    try {
      setLoading(true);
      setError(null);
      const instructorsData = await fetchInstructors();
      setInstructors(instructorsData);
      console.log("Fetched instructors:", instructorsData);
      message.success("Instructors data loaded successfully");
    } catch (error) {
      console.error("Error fetching instructors:", error);
      setError(error.message);
      
      if (error.response) {
        message.error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
      } else if (error.request) {
        message.error("Network error - Could not connect to server");
      } else {
        message.error(error.message || "Failed to fetch instructors");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeacher = async (values) => {
    try {
      setLoading(true);
      
      if (values.userPhotoFile && values.userPhotoFile[0]) {
        const file = values.userPhotoFile[0].originFileObj;
        const validation = validateFile(file, ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_MB);
        if (!validation.valid) {
          message.error(validation.message);
          setLoading(false);
          return;
        }
      }

      await registerTeacher(values);
      message.success("Teacher added successfully");
      setAddModalVisible(false);
      form.resetFields();
      loadInstructors();
    } catch (error) {
      console.error("Error adding teacher:", error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Unknown error';
        message.error(`Server error: ${error.response.status} - ${errorMessage}`);
        console.error("Server response data:", error.response.data);
      } else if (error.request) {
        message.error("Network error - Could not connect to server");
      } else {
        message.error(error.message || "Failed to add teacher");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTeacher = async (values) => {
  const accessToken = localStorage.getItem("accessToken");
  const storedDeviceId = localStorage.getItem("deviceId");
  const storedUserIp = localStorage.getItem("userIp");

  try {
    setLoading(true);
    
    // Prepare form data only for file upload
    const formData = new FormData();
    
    // Add photo file if exists
    if (values.userPhotoFile && values.userPhotoFile[0]) {
      const file = values.userPhotoFile[0].originFileObj;
      const validation = validateFile(file, ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_MB);
      if (!validation.valid) {
        message.error(validation.message);
        setLoading(false);
        return;
      }
      formData.append('userPhotoFile', file);
    }

    // Prepare query parameters
    const params = new URLSearchParams();
    params.append('userId', selectedTeacher.id);
    params.append('name', values.name);
    params.append('instructorTitle', values.instructorTitle);
    params.append('aboutInstructor', values.aboutInstructor);
    
    // Add education levels as array
    if (values.instructorTrainingEducationLevels && Array.isArray(values.instructorTrainingEducationLevels)) {
      values.instructorTrainingEducationLevels.forEach(level => {
        params.append('instructorTrainingEducationLevels', level);
      });
    }

    // Log the request for debugging
    console.log('Request params:', Object.fromEntries(params.entries()));
    console.log('Has file:', values.userPhotoFile && values.userPhotoFile[0]);

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        UserDevice: storedDeviceId,
        UserIPAddress: storedUserIp,
        'Content-Type': 'multipart/form-data'
      },
      params: params
    };

    const response = await axiosInstance.post('User/update-profile', formData, config);
    
    console.log("Update response:", response.data);
    if (response.data && response.data.success) {
      message.success("Teacher updated successfully");
      setEditModalVisible(false);
      editForm.resetFields();
      loadInstructors();
      return response.data;
    } else {
      throw new Error(response.data?.message || 'Failed to update teacher');
    }
  } catch (error) {
    console.error("Error updating teacher:", error);
    message.error(error.response?.data?.message || error.message || "Failed to update teacher");
  } finally {
    setLoading(false);
  }
};

  const beforeUpload = (file) => {
    const validation = validateFile(file, ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_MB);
    if (!validation.valid) {
      message.error(validation.message);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const viewTeacher = (teacher) => {
    setSelectedTeacher(teacher);
    setViewModalVisible(true);
  };

const editTeacher = (teacher) => {
  setSelectedTeacher(teacher);
  editForm.setFieldsValue({
    name: teacher.name || teacher.fullName || `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
    instructorTitle: teacher.instructorTitle,
    instructorTrainingEducationLevels: teacher.instructorTrainingEducationLevels,
    aboutInstructor: teacher.aboutInstructor,
    // Note: Files can't be pre-populated in upload fields
  });
  setEditModalVisible(true);
};

  const getEducationLevelLabel = (value) => {
    const level = educationLevelOptions.find(opt => opt.value === value);
    return level ? level.label : 'Unknown';
  };

  useEffect(() => {
    loadInstructors();
  }, []);

  const columns = [
    {
      title: "account_start",
      dataIndex: "account_start",
      key: "account_start",
      render: (text, record, index) => text || `T${(index + 1).toString().padStart(3, '0')}`,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => {
        if (text) return text;
        if (record.fullName) return record.fullName;
        return `${record.firstName || ''} ${record.lastName || ''}`.trim() || "N/A";
      },
    },
    {
      title: "username",
      dataIndex: "username",
      key: "username",
      render: (text, record) => {
        if (text) return text;
        if (record.username) return record.username;
        if (record.username) return record.username;
        return "0";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => viewTeacher(record)}
            style={{ marginRight: 8 }}
          >
            View
          </Button>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => editTeacher(record)}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="teacher-admin-container">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setAddModalVisible(true)}
            style={{ marginRight: 8 }}
          >
            Add Teacher
          </Button>
        </div>
        <Button 
          onClick={loadInstructors}
          loading={loading}
        >
          Refresh Data
        </Button>
      </div>
      
      {/* Add Teacher Modal */}
      <Modal
        title="Add New Teacher"
        visible={addModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddTeacher}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input the email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input style={{ height: 30 }} />
          </Form.Item>
          
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please input the full name!' }]}
          >
          <Input style={{ height: 30 }} />
          </Form.Item>
          
          <Form.Item
            name="instructorTitle"
            label="Instructor Title"
            rules={[{ required: true, message: 'Please input the title!' }]}
          >
         <Input style={{ height: 30 }} />
          </Form.Item>
          
          <Form.Item
            name="instructorTrainingEducationLevels"
            label="Education Levels"
            rules={[{ required: true, message: 'Please select at least one education level!' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select education levels"
            >
              {educationLevelOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="aboutInstructor"
            label="About Instructor"
            rules={[{ required: true, message: 'Please input information about the instructor!' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          
          <Form.Item
            name="userPhotoFile"
            label="Profile Photo"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e && e.fileList;
            }}
            rules={[
              { 
                validator: (_, fileList) => {
                  if (fileList && fileList[0]) {
                    const file = fileList[0].originFileObj;
                    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
                      return Promise.reject(new Error(`Only ${ACCEPTED_FILE_TYPES.map(t => t.split('/')[1]).join(', ')} files are allowed`));
                    }
                    if (file.size / 1024 / 1024 >= MAX_FILE_SIZE_MB) {
                      return Promise.reject(new Error(`File must be smaller than ${MAX_FILE_SIZE_MB}MB`));
                    }
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Upload
              beforeUpload={beforeUpload}
              accept={ACCEPTED_FILE_TYPES.join(',')}
              listType="picture"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>
                Upload Photo (Max {MAX_FILE_SIZE_MB}MB, JPG/PNG)
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Teacher Modal */}
      <Modal
        title="Edit Teacher"
        visible={editModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
        }}
        confirmLoading={loading}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateTeacher}
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please input the full name!' }]}
          >
            <Input style={{ height: 30 }} />
          </Form.Item>
          
          <Form.Item
            name="instructorTitle"
            label="Instructor Title"
            rules={[{ required: true, message: 'Please input the title!' }]}
          >
            <Input style={{ height: 30 }} />
          </Form.Item>
          
          <Form.Item
            name="instructorTrainingEducationLevels"
            label="Education Levels"
            rules={[{ required: true, message: 'Please select at least one education level!' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select education levels"
            >
              {educationLevelOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="aboutInstructor"
            label="About Instructor"
            rules={[{ required: true, message: 'Please input information about the instructor!' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          
          <Form.Item
            name="userPhotoFile"
            label="Profile Photo"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e && e.fileList;
            }}
            rules={[
              { 
                validator: (_, fileList) => {
                  if (fileList && fileList[0]) {
                    const file = fileList[0].originFileObj;
                    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
                      return Promise.reject(new Error(`Only ${ACCEPTED_FILE_TYPES.map(t => t.split('/')[1]).join(', ')} files are allowed`));
                    }
                    if (file.size / 1024 / 1024 >= MAX_FILE_SIZE_MB) {
                      return Promise.reject(new Error(`File must be smaller than ${MAX_FILE_SIZE_MB}MB`));
                    }
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Upload
              beforeUpload={beforeUpload}
              accept={ACCEPTED_FILE_TYPES.join(',')}
              listType="picture"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>
                Upload New Photo (Max {MAX_FILE_SIZE_MB}MB, JPG/PNG)
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Teacher Modal */}
      <Modal
        title="Teacher Details"
        visible={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedTeacher && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedTeacher.id || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Name">
              {selectedTeacher.name || selectedTeacher.fullName || 
               `${selectedTeacher.firstName || ''} ${selectedTeacher.lastName || ''}`.trim() || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">{selectedTeacher.email || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Title">{selectedTeacher.instructorTitle || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Subjects">
              {selectedTeacher.subject || 
               (selectedTeacher.subjects && Array.isArray(selectedTeacher.subjects) ? selectedTeacher.subjects.join(", ") : 'N/A') ||
               selectedTeacher.subjectTaught || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Experience">
              {selectedTeacher.experience || 
               selectedTeacher.yearsOfExperience || 
               selectedTeacher.experienceYears || 
               '0'} years
            </Descriptions.Item>
            <Descriptions.Item label="Education Levels">
              {selectedTeacher.instructorTrainingEducationLevels && Array.isArray(selectedTeacher.instructorTrainingEducationLevels) ? (
                selectedTeacher.instructorTrainingEducationLevels.map(level => (
                  <div key={level}>{getEducationLevelLabel(level)}</div>
                ))
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="About">
              {selectedTeacher.aboutInstructor || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Photo">
              {selectedTeacher.photoUrl ? (
                <Image
                  width={200}
                  src={selectedTeacher.photoUrl}
                  alt="Teacher photo"
                />
              ) : 'No photo available'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      
      <Spin spinning={loading} tip="Loading instructors...">
        {error && (
          <div style={{ color: 'red', marginBottom: 16 }}>
            Error: {error}
          </div>
        )}
        <Table 
          columns={columns} 
          dataSource={instructors}
          rowKey={(record) => record.id || Math.random().toString(36).substr(2, 9)}
          locale={{ emptyText: error ? "Error loading data" : "No instructors found" }}
        />
      </Spin>
    </div>
  );
};

export default Teachers;